import { type FormEvent, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useChangePassword } from '@/features/profile/api';
import { useAuthStore } from '@/core/auth/authStore';
import { setAccessToken, setCsrfToken } from '@/core/api/client';
import { ApiError } from '@/core/api/envelope';

/**
 * Blocking gate for temp-password logins (must_change_password=true). The
 * RouteGuard pins the user here until they set a new password. Because
 * changePassword revokes all sessions server-side, success signs the user out
 * so they re-login with the new password (flag now cleared).
 */
export function ForceChangePasswordPage() {
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const status = useAuthStore((s) => s.status);
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword);
  const changePassword = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (next.length < 8) {
      setErr('New password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    try {
      await changePassword.mutateAsync({ current_password: current, new_password: next });
      // Password change revokes all sessions — sign out and re-login.
      setAccessToken(null);
      setCsrfToken(null);
      clear();
      navigate('/login', { replace: true });
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Could not change password. Please try again.');
    }
  };

  // Reachable only by an active temp-password session; otherwise send to login.
  if (status !== 'authenticated' || !mustChangePassword) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="grid min-h-screen place-items-center p-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-xl border border-fv-border p-6">
        <div>
          <h1 className="text-xl font-bold text-fv-text">Set a new password</h1>
          <p className="mt-1 text-sm text-fv-muted">
            You signed in with a temporary password. Choose a new one to continue.
          </p>
        </div>
        <input
          type="password"
          className="fv-input w-full"
          placeholder="Temporary (current) password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <input
          type="password"
          className="fv-input w-full"
          placeholder="New password (min 8 characters)"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <input
          type="password"
          className="fv-input w-full"
          placeholder="Confirm new password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {err && <p className="text-sm text-fv-error">{err}</p>}
        <button
          type="submit"
          disabled={changePassword.isPending}
          className="w-full rounded-lg bg-fv-primary px-4 py-2 font-semibold text-white disabled:opacity-60"
        >
          {changePassword.isPending ? 'Saving…' : 'Set password & continue'}
        </button>
      </form>
    </div>
  );
}
